import type { ICarImageElement } from 'data/schema';
import { formatDateForHTML } from 'utils/date';
import { convertBlobToJpeg } from 'utils/ui/convertBlobToJpeg';
import { downloadBlob, downloadImage } from 'utils/ui/downloadImage';

const CONCURRENCY_LIMIT = 5;
export const FETCH_TIMEOUT_MS = 30_000;

/**
 * Sanitize a filename segment so it is safe for use as a zip entry name
 * and cross-platform filesystem name.
 *
 * Replaces reserved characters (`/ \ : * ? " < > |`), NUL, ASCII control
 * characters, and consecutive whitespace/dash runs with a single dash;
 * strips leading dots to prevent path traversal; trims edges; falls back
 * to `"image"` when the result is empty.
 */
export function sanitizeFilename(raw: string): string {
  if (!raw) return 'image';

  // eslint-disable-next-line no-control-regex -- deliberate: strip NUL + ASCII control chars from filenames
  const reservedReplaced = raw.replace(/[\x00-\x1f/\\:*?"<>|]/g, '-');
  const dashCollapsed = reservedReplaced.replace(/-+/g, '-');
  const leadingDotsStripped = dashCollapsed.replace(/^\.+/, '');
  const trimmed = leadingDotsStripped.replace(/^[-\s]+|[-\s]+$/g, '');

  return trimmed || 'image';
}

/**
 * Build the zip archive filename for a bulk image download.
 * Uses UTC date for i18n-neutral, filesystem-safe output.
 */
export function buildZipFilename(carName?: string): string {
  const datePart = formatDateForHTML(new Date());
  const carPart = carName ? sanitizeFilename(carName) : '';
  return carPart ? `${carPart}-${datePart}.zip` : `images-${datePart}.zip`;
}

/**
 * Produce a unique entry name for a zip archive, suffixing with `-2`, `-3`, …
 * on collision. The `seen` map is mutated to track collisions across calls
 * and is compared case-insensitively so archives remain unambiguous when
 * extracted on case-insensitive filesystems (Windows, default macOS).
 */
export function buildUniqueEntryName(name: string, seen: Map<string, number>): string {
  const key = name.toLowerCase();
  const count = seen.get(key) ?? 0;
  seen.set(key, count + 1);

  if (count === 0) return name;

  const dotIndex = name.lastIndexOf('.');
  const stem = dotIndex > 0 ? name.slice(0, dotIndex) : name;
  const ext = dotIndex > 0 ? name.slice(dotIndex) : '';
  return `${stem}-${count + 1}${ext}`;
}

/**
 * Run async tasks with bounded concurrency. Aborts remaining tasks and
 * rejects with the first failure. Results are returned in task order.
 */
async function withConcurrency<T>(
  tasks: ReadonlyArray<(signal: AbortSignal) => Promise<T>>,
  limit: number,
  externalSignal?: AbortSignal
): Promise<T[]> {
  const results = new Array<T>(tasks.length);
  const controller = new AbortController();
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort(externalSignal.reason);
    else externalSignal.addEventListener('abort', () => controller.abort(externalSignal.reason), { once: true });
  }

  let cursor = 0;
  let firstError: unknown;

  const runWorker = async (): Promise<void> => {
    while (cursor < tasks.length && firstError === undefined) {
      const index = cursor;
      cursor += 1;
      const task = tasks[index];
      try {
        results[index] = await task(controller.signal);
      } catch (error) {
        if (firstError === undefined) {
          firstError = error;
          controller.abort(error);
        }
        return;
      }
    }
  };

  const workerCount = Math.min(limit, tasks.length);
  const workers = Array.from({ length: workerCount }, () => runWorker());
  await Promise.all(workers);

  if (firstError !== undefined) throw firstError;
  return results;
}

async function fetchImageAsJpeg(url: string, signal: AbortSignal): Promise<Blob> {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Failed to fetch image ${url}: ${response.status} ${response.statusText}`);
  }
  const raw = await response.blob();
  return convertBlobToJpeg(raw);
}

// Falls back to manual composition when AbortSignal.any / AbortSignal.timeout are unavailable (older envs).
export function withTimeout(signal: AbortSignal, timeoutMs: number): AbortSignal {
  if (typeof AbortSignal.any === 'function' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.any([signal, AbortSignal.timeout(timeoutMs)]);
  }
  const controller = new AbortController();
  if (signal.aborted) controller.abort(signal.reason);
  else signal.addEventListener('abort', () => controller.abort(signal.reason), { once: true });
  const timer = setTimeout(() => controller.abort(new DOMException('Request timed out', 'TimeoutError')), timeoutMs);
  controller.signal.addEventListener('abort', () => clearTimeout(timer), { once: true });
  return controller.signal;
}

/**
 * Download multiple images as a single zip archive, or fall back to a direct
 * per-file download when there is exactly one image.
 *
 * - Video items are skipped.
 * - Empty input: no-op.
 * - Fetches run with bounded concurrency (cap 5); first failure aborts the rest.
 * - Each entry is stored uncompressed (JPEGs are already compressed).
 * - fflate is loaded via dynamic import so the library stays out of the main bundle.
 */
export async function downloadImagesAsZip(images: ICarImageElement[], carName?: string): Promise<void> {
  const imageItems = images.filter((item) => item.type === 'image');

  if (imageItems.length === 0) return;

  if (imageItems.length === 1) {
    const only = imageItems[0];
    await downloadImage(only.source, only.title, carName);
    return;
  }

  const seenNames = new Map<string, number>();
  const entryNames = imageItems.map((item) => {
    const stem = carName ? `${carName} - ${item.title || 'image'}` : item.title || 'image';
    return buildUniqueEntryName(sanitizeFilename(stem) + '.jpg', seenNames);
  });

  const tasks = imageItems.map((item) => async (signal: AbortSignal): Promise<Uint8Array> => {
    const blob = await fetchImageAsJpeg(item.source, withTimeout(signal, FETCH_TIMEOUT_MS));
    const buffer = await blob.arrayBuffer();
    return new Uint8Array(buffer);
  });

  const blobs = await withConcurrency(tasks, CONCURRENCY_LIMIT);

  const zipInput: Record<string, [Uint8Array, { level: 0 }]> = {};
  entryNames.forEach((name, index) => {
    zipInput[name] = [blobs[index], { level: 0 }];
  });

  const { zip } = await import('fflate');
  const archive = await new Promise<Uint8Array<ArrayBuffer>>((resolve, reject) => {
    zip(zipInput, { level: 0 }, (error, data) => {
      if (error) reject(error);
      else resolve(data as Uint8Array<ArrayBuffer>);
    });
  });

  downloadBlob(new Blob([archive], { type: 'application/zip' }), buildZipFilename(carName));
}
